import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { formatDate, formatEuro } from '../lib/format';

function fmtM2(c: number): string {
  return `${(c / 100).toFixed(2).replace('.', ',')} m²`;
}

function fmtM(cm: number): string {
  return `${(cm / 100).toFixed(2).replace('.', ',')} m`;
}

export function ContainerDetailPage(): JSX.Element {
  const { id } = useParams();
  const cid = id !== undefined ? Number.parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['containers', 'detail', cid],
    queryFn: () => api.containers.get(cid),
    enabled: !Number.isNaN(cid),
  });

  const reception = useMutation({
    mutationFn: () => api.containers.receptionner(cid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['containers'] });
      void queryClient.invalidateQueries({ queryKey: ['references'] });
      void queryClient.invalidateQueries({ queryKey: ['rouleaux'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setConfirmOpen(false);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(`Réception refusée : ${JSON.stringify(err.details ?? err.message)}`);
      } else {
        setError(err instanceof ApiError ? err.message : 'Erreur réception.');
      }
      setConfirmOpen(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.containers.delete(cid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['containers'] });
      navigate('/containers');
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur suppression.');
      setDeleteOpen(false);
    },
  });

  if (Number.isNaN(cid)) return <div className="text-sm text-red-400">ID invalide.</div>;
  if (query.isLoading) return <div className="text-sm text-slate-400">Chargement…</div>;
  if (query.isError || query.data === undefined)
    return <div className="text-sm text-red-400">Container introuvable.</div>;

  const { container, lignes, rouleaux, cout_total_eur_centimes, m2_total_centi } = query.data;

  const nbRouleauxPrevu = lignes.reduce((s, l) => s + l.nb_rouleaux, 0);

  return (
    <div className="max-w-6xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs text-slate-500 mb-1">Container #{container.id}</div>
          <h1 className="text-2xl font-bold text-slate-100">{container.reference_container}</h1>
          <p className="text-sm text-slate-400 mt-1">
            Commande {formatDate(container.date_commande)}
            {container.date_reception_reelle !== null
              ? ` · réceptionné le ${formatDate(container.date_reception_reelle)}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {container.statut !== 'Receptionne' && (
            <Button onClick={(): void => setConfirmOpen(true)}>Réceptionner</Button>
          )}
          {container.statut !== 'Receptionne' && (
            <Button variant="danger" onClick={(): void => setDeleteOpen(true)}>
              Supprimer
            </Button>
          )}
        </div>
      </header>

      {error !== null && (
        <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Statut" value={container.statut} />
        <Kpi label="m² prévus" value={fmtM2(m2_total_centi)} />
        <Kpi label="Rouleaux prévus" value={String(nbRouleauxPrevu)} />
        <Kpi label="Coût total HT" value={formatEuro(cout_total_eur_centimes)} />
      </div>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden mb-4">
        <h2 className="text-sm font-semibold text-slate-200 px-5 py-3 border-b border-slate-800">
          Composition
        </h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left">Référence</th>
              <th className="px-4 py-2 text-right">Largeur</th>
              <th className="px-4 py-2 text-right">Longueur</th>
              <th className="px-4 py-2 text-right">Nb rouleaux</th>
              <th className="px-4 py-2 text-right">m² ligne</th>
              <th className="px-4 py-2 text-right">Coût €/m²</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {lignes.map((l) => (
              <tr key={l.id}>
                <td className="px-4 py-2 text-slate-200">{l.reference_nom}</td>
                <td className="px-4 py-2 text-right text-slate-300">{l.largeur_cm} cm</td>
                <td className="px-4 py-2 text-right text-slate-300">
                  {fmtM(l.longueur_rouleau_cm)}
                </td>
                <td className="px-4 py-2 text-right text-slate-200">{l.nb_rouleaux}</td>
                <td className="px-4 py-2 text-right text-slate-200">{fmtM2(l.m2_centi)}</td>
                <td className="px-4 py-2 text-right text-slate-200">
                  {l.cout_eur_par_m2_centimes !== null
                    ? `${formatEuro(l.cout_eur_par_m2_centimes)}/m²`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {container.statut === 'Receptionne' && rouleaux.length > 0 && (
        <section className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <h2 className="text-sm font-semibold text-slate-200 px-5 py-3 border-b border-slate-800">
            Rouleaux créés ({rouleaux.length})
          </h2>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-right">Largeur</th>
                <th className="px-4 py-2 text-right">Initial / Restant</th>
                <th className="px-4 py-2 text-right">Coût €/m²</th>
                <th className="px-4 py-2 text-left">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rouleaux.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-slate-400 text-xs">#{r.id}</td>
                  <td className="px-4 py-2 text-right text-slate-300">{r.largeur_cm} cm</td>
                  <td className="px-4 py-2 text-right text-slate-200">
                    {fmtM(r.longueur_initiale_cm)} / {fmtM(r.longueur_restante_cm)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-200">
                    {formatEuro(r.cout_eur_par_m2_centimes)}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.statut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="mt-6 text-xs text-slate-500">
        <Link to="/containers" className="hover:text-slate-300">
          ← Retour à la liste
        </Link>
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={(): void => setConfirmOpen(false)}
        title="Confirmer la réception"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={(): void => setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={(): void => reception.mutate()}
              disabled={reception.isPending}
            >
              {reception.isPending ? 'Réception…' : 'Réceptionner'}
            </Button>
          </>
        }
      >
        <div className="text-sm text-slate-300 space-y-2">
          <p>
            Cette opération va créer <strong>{nbRouleauxPrevu}</strong> rouleau
            {nbRouleauxPrevu > 1 ? 'x' : ''} en stock, répartis sur <strong>{lignes.length}</strong>{' '}
            ligne{lignes.length > 1 ? 's' : ''} de composition.
          </p>
          <p>
            Coût total locké :{' '}
            <strong className="text-floryon-green">{formatEuro(cout_total_eur_centimes)}</strong>
          </p>
          <p className="text-xs text-amber-300">
            ⚠ La réception est définitive. Les coûts structurels ne pourront plus être modifiés
            après réception.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={deleteOpen}
        onClose={(): void => setDeleteOpen(false)}
        title="Supprimer ce container ?"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={(): void => setDeleteOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={(): void => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Cette suppression est définitive et n'est possible que tant que le container n'est pas
          réceptionné.
        </p>
      </Modal>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-100 mt-1">{value}</div>
    </div>
  );
}
